import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import TenantService from "../../services/tenantService";
import PermissionGroupService from "../../services/tenantServices/PermissionGroupService";
import { useParams } from "react-router-dom";
import TenantLayout from "../../layouts/TenantLayout";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import SecurityIcon from "@mui/icons-material/Security";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import EditIcon from "@mui/icons-material/Edit";
import TextField from "@mui/material/TextField";

export default function TenantsDoctorProfile() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('tenantDashboard');
  const { id } = useParams();
  const tenantService = new TenantService();
  const permissionService = new PermissionGroupService();
  const token = localStorage.getItem("tokenTenant");
  const [doctorInfo, setDoctorInfo] = useState({});
  const [doctorGroups, setDoctorGroups] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [allDoctors, setAllDoctors] = useState([]);
  const [subDoctors, setSubDoctors] = useState([]);
  const [editRoleTitleDialogOpen, setEditRoleTitleDialogOpen] = useState(false);
  const [roleTitleValue, setRoleTitleValue] = useState("");
  const [roleTitleError, setRoleTitleError] = useState("");

  useEffect(() => {
    const fetchDoctorInfo = async () => {
      try {
        const response = await tenantService.getDoctorInfo(id, token);
        console.log("Doctor Info Response:", response); // Debug log
        // Response might be { doctor: {...} } or just {...}
        setDoctorInfo(response?.doctor || response || {});
      } catch (error) {
        console.error("Error fetching doctor info:", error);
        setDoctorInfo({});
      }
    };
    if (id && token) {
      fetchDoctorInfo();
      fetchDoctorGroups();
      fetchAllDoctors();
    }
  }, [id, token]);

  const fetchAllDoctors = async () => {
    try {
      const response = await tenantService.getAllDoctor(token);
      // Response structure: { doctors: { admins: [{ ...admin, normalDoctors: [...] }] } }
      const allDoctorsList = [];
      
      if (response?.doctors?.admins) {
        response.doctors.admins.forEach(admin => {
          // Add admin doctor
          allDoctorsList.push({
            id: admin.id,
            name: admin.name,
            surname: admin.surname,
            email: admin.email,
            role: admin.role,
            specialization: admin.specialization,
            phone_number: admin.phone_number,
            tenant_id: admin.tenant_id,
            created_by: admin.created_by,
          });
          
          // Add normal doctors created by this admin
          if (admin.normalDoctors && Array.isArray(admin.normalDoctors)) {
            admin.normalDoctors.forEach(normalDoc => {
              allDoctorsList.push({
                id: normalDoc.id,
                name: normalDoc.name,
                surname: normalDoc.surname,
                email: normalDoc.email,
                role: normalDoc.role,
                specialization: normalDoc.specialization,
                phone_number: normalDoc.phone_number,
                tenant_id: normalDoc.tenant_id,
                created_by: normalDoc.created_by,
              });
            });
          }
        });
      }
      
      setAllDoctors(allDoctorsList);
      
      // Filter sub-doctors (doctors created by this doctor)
      // Use string comparison since IDs are UUIDs (strings)
      const subDoctorsList = allDoctorsList.filter(doc => doc.created_by === id || doc.created_by === String(id));
      setSubDoctors(subDoctorsList);
    } catch (error) {
      console.error("Error fetching all doctors:", error);
      setAllDoctors([]);
      setSubDoctors([]);
    }
  };

  const fetchDoctorGroups = async () => {
    try {
      const groups = await permissionService.getDoctorGroups(id);
      setDoctorGroups(groups);
    } catch (error) {
      console.error("Error fetching doctor groups:", error);
    }
  };

  const fetchAvailableGroups = async () => {
    try {
      const allGroups = await permissionService.getAllGroups();
      // Filter out admin groups and already assigned groups
      const available = allGroups.filter(group => 
        group.type === 'admin' && !doctorGroups.some(dg => dg.id === group.id)
      );
      setAvailableGroups(available);
    } catch (error) {
      console.error("Error fetching available groups:", error);
    }
  };

  const handleOpenAssignDialog = async () => {
    await fetchAvailableGroups();
    setAssignDialogOpen(true);
  };

  const handleAssignGroup = async (groupId) => {
    try {
      setLoading(true);
      await permissionService.assignGroupToDoctor(id, groupId);
      setSnackbar({
        open: true,
        message: t('permissionGroupAssignedSuccess'),
        severity: "success",
      });
      setAssignDialogOpen(false);
      fetchDoctorGroups();
    } catch (error) {
      const errorMsg = error.response?.data?.message || t('errorAssigningGroup');
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveGroup = async (groupId) => {
    try {
      setLoading(true);
      await permissionService.removeGroupFromDoctor(id, groupId);
      setSnackbar({
        open: true,
        message: t('permissionGroupRemovedSuccess'),
        severity: "success",
      });
      fetchDoctorGroups();
    } catch (error) {
      const errorMsg = error.response?.data?.message || t('errorRemovingGroup');
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });      
    } finally {
      setLoading(false);
    }
  };

  const capitalizeWords = (str) => {
    return str.trim().replace(/\b\w/g, char => char.toUpperCase());
  };

  const handleOpenEditRoleTitleDialog = () => {
    setRoleTitleValue(doctorInfo?.role_title || "");
    setRoleTitleError("");
    setEditRoleTitleDialogOpen(true);
  };

  const handleCloseEditRoleTitleDialog = () => {
    setEditRoleTitleDialogOpen(false);
    setRoleTitleValue("");
    setRoleTitleError("");
  };

  const handleSaveRoleTitle = async () => {
    if (!roleTitleValue.trim()) {
      setRoleTitleError(t('roleTitleRequired'));
      return;
    }

    try {
      setLoading(true);
      const capitalizedRoleTitle = capitalizeWords(roleTitleValue);
      await tenantService.updateDoctorByTenant(id, { role_title: capitalizedRoleTitle }, token);
      
      // Refresh doctor info
      const response = await tenantService.getDoctorInfo(id, token);
      setDoctorInfo(response?.doctor || response || {});
      
      setSnackbar({
        open: true,
        message: t('roleTitleUpdatedSuccess'),
        severity: "success",
      });
      handleCloseEditRoleTitleDialog();
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data?.message || t('errorUpdatingRoleTitle');
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getPatientNumber = async()=>{
    const response = await tenantService.getAllPatients()
  }

  const formattedRole = useMemo(() => {
    if (!doctorInfo?.role) return "—";
    return doctorInfo.role.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
  }, [doctorInfo]);

  // Build hierarchy tree for sub-doctors
  const hierarchyTree = useMemo(() => {
    if (!doctorInfo?.id || allDoctors.length === 0) return [];

    const nodeMap = new Map();
    
    // Add current doctor to map (ensure it's in the map even if not in allDoctors)
    nodeMap.set(String(doctorInfo.id), { ...doctorInfo, children: [] });
    
    // Add all doctors to map (convert IDs to strings for consistency)
    allDoctors.forEach((doc) => {
      const docId = String(doc.id);
      if (!nodeMap.has(docId)) {
        nodeMap.set(docId, { ...doc, children: [] });
      }
    });

    // If current doctor is a regular doctor (not admin), show parent admin and siblings
    if (doctorInfo?.role === 'doctor' && doctorInfo?.created_by) {
      const parentAdminId = String(doctorInfo.created_by);
      
      // Find parent admin in allDoctors
      const parentAdmin = allDoctors.find(doc => String(doc.id) === parentAdminId);
      
      if (parentAdmin) {
        // Add parent admin to map if not already there
        if (!nodeMap.has(parentAdminId)) {
          nodeMap.set(parentAdminId, { ...parentAdmin, children: [] });
        }
        
        // Find all doctors created by the same admin (siblings + current doctor)
        const siblings = allDoctors.filter(doc => 
          String(doc.created_by) === parentAdminId
        );
        
        // Add siblings to map and as children of parent admin
        siblings.forEach(sibling => {
          const siblingId = String(sibling.id);
          if (!nodeMap.has(siblingId)) {
            nodeMap.set(siblingId, { ...sibling, children: [] });
          }
          
          const parent = nodeMap.get(parentAdminId);
          if (parent && !parent.children.some(child => String(child.id) === siblingId)) {
            parent.children.push(nodeMap.get(siblingId));
          }
        });
        
        // Return parent admin as root
        return [nodeMap.get(parentAdminId)];
      }
    }

    // For admin doctors, build normal parent-child relationships
    nodeMap.forEach((node) => {
      if (node.created_by) {
        // Convert created_by to string for comparison
        const parentId = String(node.created_by);
        const nodeId = String(node.id);
        
        // Check if parent exists and it's not the same node
        if (parentId !== nodeId && nodeMap.has(parentId)) {
          const parent = nodeMap.get(parentId);
          if (parent && !parent.children.some(child => String(child.id) === nodeId)) {
            parent.children.push(node);
          }
        }
      }
    });

    // Return root node (current doctor) with children
    const rootId = String(doctorInfo.id);
    const root = nodeMap.get(rootId);
    return root ? [root] : [];
  }, [doctorInfo, allDoctors]);

  // Render hierarchy node recursively
  const renderNode = (node, depth = 0) => {
    const initials = `${node.name?.[0] || ""}${node.surname?.[0] || ""}`.toUpperCase();
    const isRoot = depth === 0;
    const isCurrentDoctor = String(node.id) === String(doctorInfo?.id);

    return (
      <Box key={node.id || `${node.name}-${depth}`} sx={{ ml: depth === 0 ? 0 : 2, mt: depth === 0 ? 0 : 1 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            background: isRoot
              ? `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.greenAccent[600]} 100%)`
              : isCurrentDoctor
              ? `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.blueAccent[600]} 100%)`
              : colors.primary[500],
            border: isCurrentDoctor 
              ? `2px solid ${colors.greenAccent[400]}`
              : `1px solid ${colors.grey[800]}40`,
            boxShadow: isCurrentDoctor
              ? `0 6px 18px ${colors.greenAccent[500]}50`
              : `0 6px 18px ${colors.primary[900]}30`,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            position: "relative",
          }}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              fontWeight: 700,
              background: isRoot
                ? colors.primary[500]
                : isCurrentDoctor
                ? `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`
                : `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[500]} 100%)`,
              color: colors.grey[100],
              border: isCurrentDoctor
                ? `2px solid ${colors.greenAccent[400]}`
                : `1px solid ${colors.grey[700]}40`,
            }}
          >
            {initials || "DR"}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700, color: colors.grey[100] }}>
              {node.name} {node.surname}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.grey[300], display: "block" }}>
              {node.specialization || t('roleDoctor')} · {node.role || "doctor"}
            </Typography>
          </Box>
          {isRoot && doctorInfo?.role === 'admin' && (
            <Chip
              label={t('adminDoctor')}
              size="small"
              sx={{
                backgroundColor: colors.primary[500],
                color: colors.grey[100],
                fontWeight: 700,
                border: `1px solid ${colors.grey[700]}60`,
              }}
            />
          )}
          {isCurrentDoctor && !isRoot && doctorInfo?.role !== "doctor" && (
            <Chip
              label={t('you')}
              size="small"
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                fontWeight: 700,
                border: `1px solid ${colors.greenAccent[500]}60`,
              }}
            />
          )}
          {isRoot && doctorInfo?.role === 'doctor' && (
            <Chip
              label={t('adminDoctor')}
              size="small"
              sx={{
                backgroundColor: colors.primary[500],
                color: colors.grey[100],
                fontWeight: 700,
                border: `1px solid ${colors.grey[700]}60`,
              }}
            />
          )}
        </Box>

        {node.children && node.children.length > 0 && (
          <Box
            sx={{
              ml: 2.5,
              mt: 1,
              pl: 2,
              borderLeft: `2px dashed ${colors.blueAccent[700]}`,
            }}
          >
            {node.children.map((child) => renderNode(child, depth + 1))}
          </Box>
        )}
      </Box>
    );
  };

  const overviewItems = useMemo(
    () => [
      {
        label: t('patients'),
        value: doctorInfo?.patientsCount ?? "—",
        icon: <AssignmentIndOutlinedIcon sx={{ fontSize: 24 }} />,
        gradient: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[500]} 100%)`,
      },
      {
        label: t('files'),
        value: doctorInfo?.filesCount ?? "—",
        icon: <WorkspacesOutlinedIcon sx={{ fontSize: 24 }} />,
        gradient: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.blueAccent[700]} 100%)`,
      },
      {
        label: t('role'),
        value: formattedRole,
        icon: <LocalHospitalOutlinedIcon sx={{ fontSize: 24 }} />,
        gradient: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[600]} 100%)`,
      },
      {
        label: t('roleTitle'),
        value: doctorInfo?.role_title ?? "—",
        icon: <WorkspacePremiumIcon sx={{ fontSize: 24 }} />,
        gradient: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.primary[600]} 100%)`,
      },
    ],
    [colors, doctorInfo, formattedRole, t]
  );

  return (
    <TenantLayout
      contentSx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        backgroundColor: "#f5f7fb",
        pb: { xs: 6, md: 8 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          margin: "0 auto",
          maxWidth: "1100px",
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <Card
          sx={{
            borderRadius: 4,
            background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.blueAccent[800]} 100%)`,
            color: colors.grey[100],
            boxShadow: `0 24px 48px rgba(15, 23, 42, 0.35)`,
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 4 }}>
            <Stack
              alignItems="center"
              spacing={2.5}
              sx={{
                flexShrink: 0,
                minWidth: { xs: "auto", md: 260 },
              }}
            >
              <Avatar
                src={`../../assets/avatarPhoto.jpg`}
                alt="Doctor Avatar"
                sx={{
                  width: 140,
                  height: 140,
                  borderRadius: "32px",
                  boxShadow: `0 16px 40px rgba(15, 23, 42, 0.35)`,
                  border: `4px solid rgba(255,255,255,0.2)`
                }}
              />
              <Box textAlign="center">
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {doctorInfo?.name ? `${doctorInfo.name} ${doctorInfo.surname ?? ""}` : "Doctor"}
                </Typography>
                {doctorInfo?.role_title && (
                  <Typography
                    variant="h6"
                    sx={{
                      mt: 1,
                      color: colors.grey[200],
                      fontWeight: 600,
                      letterSpacing: 0.3,
                    }}
                  >
                    {doctorInfo.role_title}
                  </Typography>
                )}
                <Chip
                  label={formattedRole}
                  sx={{
                    mt: 1.5,
                    px: 2.5,
                    py: 0.5,
                    fontWeight: 600,
                    letterSpacing: 0.4,
                    color: colors.grey[100],
                    background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[600]} 100%)`,
                    boxShadow: `0 10px 24px rgba(34, 197, 94, 0.45)`,
                  }}
                />
              </Box>
            </Stack>

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderColor: `${colors.primary[300]}55`,
                borderWidth: 1,
                display: { xs: "none", md: "block" },
              }}
            />

            <Grid container spacing={3} sx={{ flexGrow: 1 }}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="overline" sx={{ color: `${colors.grey[100]}99`, letterSpacing: 1 }}>
                      {t('contact')}
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center" mt={0.8}>
                      <Avatar
                        sx={{
                          width: 42,
                          height: 42,
                          backgroundColor: `${colors.blueAccent[500]}88`,
                        }}
                      >
                        <PersonOutlinedIcon sx={{ color: colors.grey[100] }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
                          {t('fullName')}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {doctorInfo?.name} {doctorInfo?.surname}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center" mt={2}>
                      <Avatar
                        sx={{
                          width: 42,
                          height: 42,
                          backgroundColor: `${colors.blueAccent[500]}88`,
                        }}
                      >
                        <EmailOutlinedIcon sx={{ color: colors.grey[100] }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
                          {t('columnEmail')}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {doctorInfo?.email ?? "—"}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="overline" sx={{ color: `${colors.grey[100]}99`, letterSpacing: 1 }}>
                      {t('specialization')}
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center" mt={0.8}>
                      <Avatar
                        sx={{
                          width: 42,
                          height: 42,
                          backgroundColor: `${colors.greenAccent[500]}88`,
                        }}
                      >
                        <LocalHospitalOutlinedIcon sx={{ color: colors.grey[100] }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
                          {t('field')}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {doctorInfo?.specialization ?? "—"}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          {overviewItems.map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item.label}>
              <Card
                sx={{
                  borderRadius: 4,
                  height: "100%",
                  background: item.gradient,
                  color: colors.grey[100],
                  boxShadow: `0 22px 44px rgba(15, 23, 42, 0.3)`,
                  position: "relative",
                }}
              >
                {item.label === t('roleTitle') && (
                  <Tooltip title={t('editRoleTitle')}>
                    <IconButton
                      size="small"
                      onClick={handleOpenEditRoleTitleDialog}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: colors.grey[100],
                        backgroundColor: "rgba(255,255,255,0.15)",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.25)",
                        },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        backgroundColor: "rgba(255,255,255,0.25)",
                        color: "white",
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      {item.icon}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ opacity: 0.85 }}>
                        {item.label}
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        {item.value}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Team Hierarchy Section - Different for Admin and Doctor roles */}
        <Card
          sx={{
            borderRadius: 4,
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            border: `1px solid ${colors.grey[800]}30`,
            boxShadow: `0 22px 44px rgba(15, 23, 42, 0.3)`,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    background: doctorInfo?.role === 'admin' 
                      ? `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`
                      : `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[700]} 100%)`,
                  }}
                >
                  <PersonOutlinedIcon sx={{ color: "white", fontSize: 24 }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ color: colors.grey[100], fontWeight: 700 }}>
                    {t('teamHierarchy')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.grey[300], mt: 0.5 }}>
                    {doctorInfo?.role === 'admin' 
                      ? t('subDoctorsCreated')
                      : t('doctorInfoTeamStructure')}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ mb: 3, backgroundColor: `${colors.grey[700]}40` }} />

            {hierarchyTree.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  px: 3,
                  borderRadius: "12px",
                  background: `${colors.blueAccent[800]}10`,
                  border: `1px dashed ${colors.grey[500]}50`,
                }}
              >
                <PersonOutlinedIcon sx={{ fontSize: 48, color: "#94a3b8", mb: 1 }} />
                <Typography variant="h6" sx={{ color: "#475569", mb: 0.5 }}>
                  {t('noInformationAvailable')}
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  {t('unableToLoadTeamHierarchy')}
                </Typography>
              </Box>
            ) : hierarchyTree[0]?.children?.length === 0 ? (
              // Show only the doctor itself if no children
              <Box sx={{ maxHeight: 400, overflowY: "auto", pr: 1 }}>
                {hierarchyTree.map((node) => renderNode(node))}
              </Box>
            ) : (
              // Show full hierarchy if children exist
              <Box sx={{ maxHeight: 400, overflowY: "auto", pr: 1 }}>
                {hierarchyTree.map((node) => renderNode(node))}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Permission Groups Section */}
        <Card
          sx={{
            borderRadius: 4,
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            border: `1px solid ${colors.grey[800]}30`,
            boxShadow: `0 22px 44px rgba(15, 23, 42, 0.3)`,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[700]} 100%)`,
                  }}
                >
                  <SecurityIcon sx={{ color: "white", fontSize: 24 }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ color: colors.grey[100], fontWeight: 700 }}>
                    {t('permissionGroups')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.grey[300], mt: 0.5 }}>
                    {t('assignedPermissionGroups')}
                  </Typography>
                  {doctorGroups.length > 1 && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: colors.greenAccent[300], 
                        mt: 1, 
                        display: "block",
                        fontStyle: "italic",
                        fontSize: "0.75rem"
                      }}
                    >
                      {t('multipleGroupsInfo')}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenAssignDialog}
                sx={{
                  background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
                  color: "white",
                  px: 3,
                  py: 1,
                  borderRadius: "10px",
                  fontWeight: 600,
                  textTransform: "none",
                  boxShadow: `0 4px 12px ${colors.greenAccent[500]}40`,
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 6px 16px ${colors.greenAccent[500]}60`,
                  },
                  transition: "all 0.3s ease",
                }}
                >
                  {t('assignGroup')}
                </Button>
            </Box>

            <Divider sx={{ mb: 3, backgroundColor: `${colors.grey[700]}40` }} />

            {doctorGroups.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  px: 3,
                  borderRadius: "12px",
                  background: `${colors.blueAccent[800]}10`,
                  border: `1px dashed ${colors.grey[500]}50`,
                }}
              >
                <SecurityIcon sx={{ fontSize: 48, color: "#94a3b8", mb: 1 }} />
                <Typography variant="h6" sx={{ color: "#475569", mb: 0.5 }}>
                  {t('noPermissionGroupsAssigned')}
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  {t('noPermissionGroupsAssignedDescription')}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {doctorGroups.map((group) => (
                  <Grid item xs={12} sm={6} md={4} key={group.id}>
                    <Card
                      sx={{
                        borderRadius: "12px",
                        background: "white",
                        border: `1px solid ${colors.grey[300]}`,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: `0 8px 24px rgba(0, 0, 0, 0.15)`,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                                background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[700]} 100%)`,
                              }}
                            >
                              <AdminPanelSettingsIcon sx={{ color: "white", fontSize: 20 }} />
                            </Avatar>
                            <Box>
                              <Typography variant="h6" sx={{ color: "#1e293b", fontWeight: 700, fontSize: "0.95rem" }}>
                                {group.label}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#64748b", fontFamily: "monospace" }}>
                                {group.name}
                              </Typography>
                            </Box>
                          </Box>
                          {!group.is_default && (
                            <Tooltip title={t('removeGroup')}>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveGroup(group.id)}
                                disabled={loading}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  backgroundColor: `${colors.redAccent[700]}20`,
                                  "&:hover": {
                                    backgroundColor: `${colors.redAccent[700]}40`,
                                  },
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 16, color: colors.redAccent[500] }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        {group.description && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#475569",
                              fontSize: "0.8rem",
                              lineHeight: 1.5,
                            }}
                          >
                            {group.description}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Assign Group Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            border: `1px solid ${colors.grey[800]}30`,
            borderRadius: "16px",
          },
        }}
      >
        <DialogTitle sx={{ color: "#1e293b", fontWeight: 700 }}>
          {t('assignPermissionGroup')}
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={40} sx={{ color: colors.blueAccent[500] }} />
            </Box>
          ) : availableGroups.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <SecurityIcon sx={{ fontSize: 48, color: "#94a3b8", mb: 1 }} />
              <Typography variant="body1" sx={{ color: "#475569" }}>
                {t('noAvailablePermissionGroups')}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2} sx={{ mt: 2 }}>
              {availableGroups.map((group) => (
                <Card
                  key={group.id}
                  onClick={() => handleAssignGroup(group.id)}
                  sx={{
                    cursor: "pointer",
                    borderRadius: "12px",
                    background: "white",
                    border: `1px solid ${colors.grey[300]}`,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "translateX(4px)",
                      boxShadow: `0 4px 12px rgba(0, 0, 0, 0.15)`,
                      borderColor: colors.blueAccent[500],
                    },
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[700]} 100%)`,
                        }}
                      >
                        <AdminPanelSettingsIcon sx={{ color: "white", fontSize: 22 }} />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ color: "#1e293b", fontWeight: 700, fontSize: "0.95rem" }}>
                          {group.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                          {group.description || group.name}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setAssignDialogOpen(false)}
            sx={{
              color: "#64748b",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Title Dialog */}
      <Dialog
        open={editRoleTitleDialogOpen}
        onClose={handleCloseEditRoleTitleDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            border: `1px solid ${colors.grey[800]}30`,
            borderRadius: "16px",
          },
        }}
      >
        <DialogTitle sx={{ color: colors.grey[100], fontWeight: 700 }}>
          {t('editRoleTitle')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('roleTitle') + ' *'}
            value={roleTitleValue}
            onChange={(e) => {
              setRoleTitleValue(e.target.value);
              setRoleTitleError("");
            }}
            onBlur={(e) => {
              const value = e.target.value;
              if (value.trim()) {
                const capitalizedValue = capitalizeWords(value);
                setRoleTitleValue(capitalizedValue);
              }
            }}
            error={Boolean(roleTitleError)}
            helperText={roleTitleError || t('roleTitleHelper')}
            variant="filled"
            sx={{
              mt: 2,
              "& .MuiInputLabel-root": {
                color: `${colors.grey[100]}aa`,
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: colors.blueAccent[400],
              },
              "& .MuiFilledInput-root": {
                backgroundColor: `${colors.primary[600]}cc`,
                borderRadius: 2,
                color: colors.grey[100],
                "&:hover": {
                  backgroundColor: `${colors.primary[600]}ee`,
                },
                "&.Mui-focused": {
                  backgroundColor: `${colors.primary[600]}ff`,
                  boxShadow: `0 0 0 3px ${colors.blueAccent[400]}33`,
                },
              },
              "& .MuiFilledInput-input": {
                color: colors.grey[100],
              },
              "& .MuiFormHelperText-root": {
                color: `${colors.grey[100]}aa`,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseEditRoleTitleDialog}
            disabled={loading}
            sx={{
              color: colors.grey[300],
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSaveRoleTitle}
            disabled={loading}
            variant="contained"
            sx={{
              background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
              color: "white",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                background: `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.greenAccent[800]} 100%)`,
              },
              "&:disabled": {
                background: colors.grey[700],
              },
            }}
          >
            {loading ? t('saving') : t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </TenantLayout>
  );
}
